import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import type { AnimatePresenceProps, HTMLMotionProps } from 'motion/react'
import * as m from 'motion/react-m'

import { Digit } from './Digit'
import { useMeasureSection } from './hooks/useMeasureSection'
import type { Justify, NumberSegment } from './types'
import { useIsFirstRender } from './hooks/useIsFirstRender'

import type { JSX } from 'react'

export type NumberSectionProps = Omit<HTMLMotionProps<'span'>, 'children'> & {
  segments: NumberSegment[]
  justify?: Justify
  mode?: AnimatePresenceProps['mode']
  name?: string
  layoutDependency?: unknown
}

/**
 * Renders a section of animated numbers with proper layout and measurements
 */
export const NumberSection = React.forwardRef<
  HTMLSpanElement,
  NumberSectionProps
>(
  (
    {
      segments,
      justify = 'left',
      mode,
      style,
      name,
      layoutDependency,
      ...rest
    },
    _ref,
  ): JSX.Element => {
    const ref = React.useRef<HTMLSpanElement>(null)
    React.useImperativeHandle(_ref, () => {
      if (!ref.current) {
        throw new Error('The number section element is not available')
      }
      return ref.current
    }, [])

    const measuredRef = React.useRef<HTMLSpanElement>(null)
    const { isFirstRender } = useIsFirstRender()
    const width = useMeasureSection(measuredRef, isFirstRender, segments)

    // Memoize the segments rendering to prevent unnecessary re-renders
    const renderedSegments = React.useMemo(
      () =>
        segments.map((segment) =>
          segment.type === 'symbol' ? (
            <m.span
              key={segment.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              layoutDependency={layoutDependency}
              style={{
                display: 'inline-flex',
                paddingRight: '1px',
                position: 'relative',
                alignItems: 'center',
              }}
            >
              -
            </m.span>
          ) : (
            <Digit
              key={segment.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              value={segment.value}
              initialValue={isFirstRender ? undefined : 0}
              layoutDependency={layoutDependency}
              justify={justify}
            />
          ),
        ),
      [segments, isFirstRender, layoutDependency, justify],
    )

    return (
      <m.span
        layoutDependency={layoutDependency}
        {...rest}
        ref={ref}
        className={name ? `number-section-${name}` : 'number-section'}
        style={{
          ...style,
          display: 'inline-flex',
          justifyContent: justify,
          width,
        }}
      >
        <span
          ref={measuredRef}
          style={{
            display: 'inline-flex',
            justifyContent: 'inherit',
            position: 'relative',
          }}
        >
          {/* Prevent layout collapse during measurement */}
          &#8203;
          <AnimatePresence mode={mode} anchorX={justify} initial={false}>
            {renderedSegments}
          </AnimatePresence>
        </span>
      </m.span>
    )
  },
)

NumberSection.displayName = 'NumberSection'
